import { createContext, useContext } from 'react';
import { useBuilderData } from './useBuilderData';

/**
 * Shared context for builder data. BuilderDataProvider calls useBuilderData
 * exactly once and exposes the result so BuilderTopBar (and other deep
 * children) can read `data` / `updateName` without prop-drilling.
 */
const BuilderDataContext = createContext(null);

export function useBuilderDataContext() {
  return useContext(BuilderDataContext);
}

/**
 * Calls useBuilderData once and provides it to children via context.
 * Drop this at the top of each builder view, wrapping the layout wrapper:
 *
 *   <BuilderDataProvider>
 *     <ParentWrapper isBuilder>
 *       <BuilderTopBar />        // reads name + updateName from context
 *       ...
 *     </ParentWrapper>
 *   </BuilderDataProvider>
 *
 * Supports a render-prop so the builder can destructure data/mutateConfig/etc.
 * directly without calling useBuilderData itself:
 *
 *   <BuilderDataProvider>
 *     {({ data, status, mutateConfig }) => ( ... )}
 *   </BuilderDataProvider>
 */
export function BuilderDataProvider({ skipCreate, children }) {
  const builderData = useBuilderData({ skipCreate });
  return (
    <BuilderDataContext.Provider value={builderData}>
      {typeof children === 'function' ? children(builderData) : children}
    </BuilderDataContext.Provider>
  );
}
